import React from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { useSettings } from "../../../shared/providers/SettingsProvider";
import * as coreApi from "../../../shared/core-api-client";
import { useAuth } from "../../../auth/AuthContext";

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export interface SharedWithAutocompleteProps {
    value: string[];
    onChange: (sharedWith: string[]) => void;
}

// Email picker for snapshot sharing: offers the tenant's user directory when the caller
// may list users, and degrades to free-text email entry when that lookup is denied...
export function SharedWithAutocomplete(props: SharedWithAutocompleteProps) {
    const { value, onChange } = props;
    const settings = useSettings();
    const [knownUsers, setKnownUsers] = React.useState<string[]>([]);
    const { userMetadata } = useAuth();

    React.useEffect(() => {
        const shouldIncludeUser = (emailAddress: string) =>
            Boolean(emailAddress) &&
            emailAddress !== userMetadata.email &&
            !emailAddress.includes("*");

        new coreApi.UsersClient(settings.coreApiUrl)
            .search(undefined, undefined, undefined, undefined, undefined, settings.tenantId)
            .then(users => setKnownUsers(users.map(user => user.emailAddress).filter(shouldIncludeUser).toSorted()))
            .catch(() => setKnownUsers([]));
    }, []);

    return (
        <Autocomplete
            multiple
            freeSolo
            options={knownUsers}
            value={value}
            onChange={(_event, values) => onChange(values.map(entry => entry.trim().toLowerCase()).filter(entry => EMAIL_PATTERN.test(entry)))}
            renderValue={(values, getTagProps) =>
                values.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} />)
            }
            renderInput={params => (
                <TextField
                    {...params}
                    margin="dense"
                    label="Share with"
                    placeholder="Add people by email..."
                    variant="outlined"
                    size="small"
                />
            )}
        />
    );
}
